/**
 * Connect Page - Handles seamless device linking
 * 
 * URL: /connect?session=ABC-123-DEF
 * 
 * Flow:
 * 1. Page loads with session in URL
 * 2. Check if user is logged in
 * 3. If not logged in → redirect to signin, then come back
 * 4. If logged in → show "Connect Desktop" button
 * 5. User clicks button → link session to their account
 * 6. Desktop receives auth and syncs
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Loader2, CheckCircle2, Laptop, AlertCircle } from 'lucide-react';

export function ConnectPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get auth state from Convex
  const user = useQuery(api.users.getCurrentUser);
  const isLoading = user === undefined;
  const isAuthenticated = user !== null;
  
  const sessionId = searchParams.get('session');
  const [status, setStatus] = useState<'checking' | 'login-required' | 'ready' | 'linking' | 'success' | 'error'>('checking');
  const [errorMessage, setErrorMessage] = useState('');

  const linkSession = useMutation(api.deviceLink.linkSessionToUser);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      setErrorMessage('No session ID provided. Please try again from the desktop app.');
      return;
    }

    if (isLoading) return;

    if (!isAuthenticated) {
      // Redirect to login, then come back here
      const returnUrl = encodeURIComponent(`/connect?session=${sessionId}`);
      navigate(`/signin?redirect=${returnUrl}`);
      return;
    }

    setStatus('ready');
  }, [sessionId, isAuthenticated, isLoading, navigate]);

  const handleConnect = async () => {
    if (!sessionId) return;
    
    setStatus('linking');
    try {
      await linkSession({ sessionId });
      setStatus('success');
      
      // Auto-close after 3 seconds if in popup
      setTimeout(() => {
        if (window.opener) {
          window.close();
        }
      }, 3000);
    } catch (err) {
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Failed to connect device');
    }
  };

  if (status === 'checking' || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="flex items-center gap-2 text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Checking session...</span>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center gap-2 text-red-500 mb-4">
            <AlertCircle className="w-6 h-6" />
            <h2 className="text-xl font-semibold">Connection Failed</h2>
          </div>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <button 
            onClick={() => navigate('/')}
            className="w-full bg-gray-900 text-white py-2 px-4 rounded hover:bg-gray-800 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6 text-center">
          <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Desktop Connected!</h2>
          <p className="text-gray-600 mb-6">
            Your desktop app is now linked and syncing with your cloud account.
          </p>
          <p className="text-sm text-gray-500 mb-4">
            You can close this window and return to the desktop app.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="w-full bg-gray-900 text-white py-2 px-4 rounded hover:bg-gray-800 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
            <Laptop className="w-8 h-8 text-teal-600" />
          </div>
          <h2 className="text-xl font-semibold mb-1">Connect Desktop App</h2>
          <p className="text-gray-600">
            Link your desktop app to sync across all your devices.
          </p>
        </div>

        {sessionId && (
          <div className="bg-gray-100 p-3 rounded-lg text-center mb-6">
            <p className="text-xs text-gray-500 mb-1">Session ID</p>
            <code className="text-lg font-mono font-bold text-gray-900">{sessionId}</code>
          </div>
        )}

        <div className="text-sm text-gray-600 mb-6 space-y-2">
          <p>This will:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Link your desktop app to this account</li>
            <li>Enable automatic cloud sync</li>
            <li>Allow access to all your projects and assets</li>
          </ul>
        </div>

        <div className="space-y-3">
          <button 
            onClick={handleConnect} 
            disabled={status === 'linking'}
            className="w-full bg-teal-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {status === 'linking' ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              'Connect Desktop App'
            )}
          </button>

          <button 
            onClick={() => navigate('/')}
            disabled={status === 'linking'}
            className="w-full bg-white text-gray-700 border border-gray-300 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConnectPage;
